import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

import { BlockWithRevisions, BlockRevision, MissedReason, MISSED_REASON_LABELS } from '@/types';
import { DURATION_OPTIONS, CATEGORY_COLORS } from '@/constants/categories';
import styles from './BlockDetailContent.module.css';

export interface BlockUpdateData {
  title?: string;
  date?: string;
  startTime?: number;
  duration?: number;
  categoryId?: string;
  color?: string;
  notes?: string;
  status?: 'planned' | 'completed' | 'missed' | 'cancelled';
  missedReason?: MissedReason;
}

const CATEGORIES = [
  { id: 'deep-work', name: 'Deep Work', color: '#2b6cb0' },
  { id: 'shallow', name: 'Shallow', color: '#9f7aea' },
  { id: 'personal', name: 'Personal', color: '#38a169' },
  { id: 'meeting', name: 'Meeting', color: '#ed8936' },
];

function formatTimeFromMinutes(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const date = new Date();
  date.setHours(hours, mins, 0, 0);
  return format(date, 'h:mm a');
}

// Convert minutes to 24-hour format for input type="time"
function minutesToTimeInput(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

// Convert time input string to minutes
function timeInputToMinutes(timeStr: string): number {
  const [hours, mins] = timeStr.split(':').map(Number);
  return hours * 60 + mins;
}

function formatDuration(minutes: number) {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

interface BlockDetailContentProps {
  block: BlockWithRevisions | null;
  onClose: () => void;
  onSave: (blockId: string, updates: BlockUpdateData) => Promise<boolean>;
  onDelete: (blockId: string) => Promise<boolean>;
  onMarkComplete: (blockId: string) => Promise<boolean>;
  onMarkMissed: (blockId: string, reason?: MissedReason) => Promise<boolean>;
  onDuplicate?: (block: BlockWithRevisions) => void;
}

export function BlockDetailContent({
  block,
  onClose,
  onSave,
  onDelete,
  onMarkComplete,
  onMarkMissed,
  onDuplicate,
}: BlockDetailContentProps) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState(0);
  const [duration, setDuration] = useState(30);
  const [categoryId, setCategoryId] = useState('deep-work');
  const [color, setColor] = useState('#2b6cb0');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showMissedSelector, setShowMissedSelector] = useState(false);
  const [selectedMissedReason, setSelectedMissedReason] = useState<MissedReason | null>(null);

  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (block) {
      setTitle(block.title);
      setDate(block.date);
      setStartTime(block.startTime);
      setDuration(block.duration);
      setCategoryId(block.categoryId);
      setColor(block.color);
      setNotes(block.notes || '');
      setShowDeleteConfirm(false);
      setShowTimePicker(false);
      setShowMissedSelector(false);
      setSelectedMissedReason(null);
    }
  }, [block]);

  useEffect(() => {
    if (block && titleInputRef.current) {
      const timer = setTimeout(() => {
        titleInputRef.current?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [block]);

  const handleSave = useCallback(async () => {
    if (!block || !title.trim()) return;

    setIsSaving(true);
    try {
      const updates: BlockUpdateData = {};

      if (title.trim() !== block.title) updates.title = title.trim();
      if (date !== block.date) updates.date = date;
      if (startTime !== block.startTime) updates.startTime = startTime;
      if (duration !== block.duration) updates.duration = duration;
      if (categoryId !== block.categoryId) updates.categoryId = categoryId;
      if (color !== block.color) updates.color = color;
      if (notes.trim() !== (block.notes || '')) updates.notes = notes.trim();

      if (Object.keys(updates).length > 0) {
        const success = await onSave(block.id, updates);
        if (!success) {
          console.error('Failed to save block - operation returned false');
          return; // Don't close on failure
        }
      }
      onClose();
    } catch (error) {
      console.error('Failed to save block:', error);
      // Don't close on error - let user retry or cancel
    } finally {
      setIsSaving(false);
    }
  }, [block, title, startTime, duration, categoryId, color, notes, onSave, onClose]);

  const handleDelete = useCallback(async () => {
    if (!block) return;

    setIsDeleting(true);
    try {
      const success = await onDelete(block.id);
      if (!success) {
        console.error('Failed to delete block - operation returned false');
        return;
      }
      onClose();
    } catch (error) {
      console.error('Failed to delete block:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [block, onDelete, onClose]);

  const handleMarkComplete = useCallback(async () => {
    if (!block) return;

    try {
      const success = await onMarkComplete(block.id);
      if (!success) {
        console.error('Failed to mark block complete - operation returned false');
        return;
      }
      onClose();
    } catch (error) {
      console.error('Failed to mark block complete:', error);
    }
  }, [block, onMarkComplete, onClose]);

  const handleReopen = useCallback(async () => {
    if (!block) return;
    try {
      const success = await onSave(block.id, { status: 'planned' });
      if (success) onClose();
    } catch (error) {
      console.error('Failed to reopen block:', error);
    }
  }, [block, onSave, onClose]);

  const handleDuplicate = useCallback(() => {
    if (!block || !onDuplicate) return;
    onDuplicate(block);
    onClose();
  }, [block, onDuplicate, onClose]);

  const handleMarkMissed = useCallback(async () => {
    if (!block) return;

    try {
      const success = await onMarkMissed(block.id, selectedMissedReason || undefined);
      if (!success) {
        console.error('Failed to mark block as missed - operation returned false');
        return;
      }
      onClose();
    } catch (error) {
      console.error('Failed to mark block as missed:', error);
    }
  }, [block, onMarkMissed, selectedMissedReason, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
        } else if (showTimePicker) {
          setShowTimePicker(false);
        } else if (showMissedSelector) {
          setShowMissedSelector(false);
        } else {
          onClose();
        }
      } else if (e.key === 'Enter' && e.metaKey) {
        handleSave();
      }
    },
    [onClose, handleSave, showDeleteConfirm, showTimePicker, showMissedSelector]
  );

  const adjustTime = useCallback((delta: number) => {
    setStartTime((prev) => {
      const next = prev + delta;
      if (next < 0) return 1440 + next;
      if (next >= 1440) return next - 1440;
      return next;
    });
  }, []);

  useEffect(() => {
    const category = CATEGORIES.find((c) => c.id === categoryId);
    if (category) {
      setColor(category.color);
    }
  }, [categoryId]);

  if (!block) return null;

  const isCompleted = block.status === 'completed';
  const isCancelled = block.status === 'cancelled';
  const isMissed = block.status === 'missed';
  const hasRevisions = block.revisions && block.revisions.length > 0;

  const getStatusBannerClass = () => {
    if (isCompleted) return styles.completed;
    if (isMissed) return styles.missed;
    return styles.cancelled;
  };

  const renderStatusBannerContent = () => {
    if (isCompleted) return '✓ Completed';
    if (isMissed) {
      return (
        <>
          ○ Missed
          {block.missedReason && (
            <span className={styles.missedReasonBanner}>
              ({MISSED_REASON_LABELS[block.missedReason]})
            </span>
          )}
        </>
      );
    }
    return '✗ Cancelled';
  };

  return (
    <div className={styles.container} onKeyDown={handleKeyDown}>
      {/* Status Banner */}
      {(isCompleted || isCancelled || isMissed) && (
        <div className={`${styles.statusBanner} ${getStatusBannerClass()}`}>
          <span>{renderStatusBannerContent()}</span>
          <button className={styles.reopenBtn} onClick={handleReopen}>
            ↻ Reopen
          </button>
        </div>
      )}

      {/* Quick Actions */}
      {!isCompleted && !isCancelled && !isMissed && (
        <div className={styles.quickActions}>
          <button className={styles.markDoneBtn} onClick={handleMarkComplete}>
            ✓ Mark Done
          </button>
          <button
            className={styles.markMissedBtn}
            onClick={() => setShowMissedSelector(!showMissedSelector)}
          >
            ○ Mark Missed
          </button>

          {/* Missed Reason Selector */}
          {showMissedSelector && (
            <div className={styles.reasonSelector}>
              <span className={styles.reasonSelectorLabel}>Why was this missed? (optional)</span>
              <div className={styles.reasonOptions}>
                {(Object.keys(MISSED_REASON_LABELS) as MissedReason[]).map((reason) => (
                  <button
                    key={reason}
                    className={`${styles.reasonChip} ${selectedMissedReason === reason ? styles.reasonChipActive : ''}`}
                    aria-pressed={selectedMissedReason === reason}
                    onClick={() =>
                      setSelectedMissedReason(selectedMissedReason === reason ? null : reason)
                    }
                  >
                    {MISSED_REASON_LABELS[reason]}
                  </button>
                ))}
              </div>
              <div className={styles.reasonActions}>
                <button
                  className={styles.cancelMissedBtn}
                  onClick={() => setShowMissedSelector(false)}
                >
                  Cancel
                </button>
                <button className={styles.confirmMissedBtn} onClick={handleMarkMissed}>
                  Confirm Missed
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Form */}
      <div className={styles.form}>
        {/* Title */}
        <div className={styles.field}>
          <input
            id="block-detail-input-title"
            ref={titleInputRef}
            type="text"
            className={styles.titleInput}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What are you working on?"
          />
        </div>

        {/* Date */}
        <div className={styles.field}>
          <label className={styles.label}>Date</label>
          <input
            type="date"
            className={styles.dateInput}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        {/* Time Controls */}
        <div className={styles.field}>
          <label className={styles.label}>Start Time</label>
          <div className={styles.timeControls}>
            <button className={styles.timeBtn} onClick={() => adjustTime(-15)} title="−15 min">
              −
            </button>
            <input
              type="time"
              className={styles.timeInput}
              value={minutesToTimeInput(startTime)}
              onChange={(e) => setStartTime(timeInputToMinutes(e.target.value))}
            />
            <button className={styles.timeBtn} onClick={() => adjustTime(15)} title="+15 min">
              +
            </button>
          </div>
        </div>

        {/* End Time */}
        <div className={styles.field}>
          <label className={styles.label}>End Time</label>
          <div className={styles.timeControls}>
            <button
              className={styles.timeBtn}
              onClick={() => setDuration((prev) => Math.max(15, prev - 15))}
              title="−15 min"
            >
              −
            </button>
            <input
              type="time"
              className={styles.timeInput}
              value={minutesToTimeInput(startTime + duration)}
              onChange={(e) => {
                const endMinutes = timeInputToMinutes(e.target.value);
                const newDuration = endMinutes - startTime;
                if (newDuration > 0) {
                  setDuration(newDuration);
                }
              }}
            />
            <button
              className={styles.timeBtn}
              onClick={() => setDuration((prev) => Math.min(480, prev + 15))}
              title="+15 min"
            >
              +
            </button>
            <span className={styles.timeDuration}>({formatDuration(duration)})</span>
          </div>
        </div>

        {/* Duration */}
        <div className={styles.field}>
          <label className={styles.label}>Duration</label>
          <div className={styles.chipRow}>
            {DURATION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.chip} ${duration === opt.value ? styles.chipActive : ''}`}
                onClick={() => setDuration(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Category */}
        <div className={styles.field}>
          <label className={styles.label}>Category</label>
          <div className={styles.chipRow}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`${styles.categoryChip} ${categoryId === cat.id ? styles.categoryActive : ''}`}
                style={
                  {
                    '--cat-color': cat.color,
                  } as React.CSSProperties
                }
                onClick={() => setCategoryId(cat.id)}
              >
                <span className={styles.categoryDot} style={{ background: cat.color }} />
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Color */}
        <div className={styles.field}>
          <label className={styles.label}>Color</label>
          <div className={styles.colorRow}>
            {CATEGORY_COLORS.map((c) => (
              <button
                key={c}
                className={`${styles.colorSwatch} ${color === c ? styles.colorActive : ''}`}
                style={{ background: c }}
                onClick={() => setColor(c)}
              >
                {color === c && <span>✓</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className={styles.field}>
          <label className={styles.label}>Notes</label>
          <textarea
            id="block-detail-notes"
            className={styles.notesInput}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add details..."
            rows={3}
          />
        </div>

        {/* Revisions */}
        {hasRevisions && (
          <div className={styles.field}>
            <div className={styles.revisionHeader}>
              <label className={styles.label}>History</label>
              <span className={styles.revisionCount}>{block.revisions.length}</span>
            </div>
            <div className={styles.revisionList}>
              {block.revisions.slice(0, 2).map((revision, index) => (
                <RevisionItem key={revision.id} revision={revision} index={index} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className={styles.footer}>
        {showDeleteConfirm ? (
          <div className={styles.deleteConfirm}>
            <span>Delete this block?</span>
            <button
              className={styles.confirmDeleteBtn}
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? '...' : 'Delete'}
            </button>
            <button className={styles.cancelDeleteBtn} onClick={() => setShowDeleteConfirm(false)}>
              No
            </button>
          </div>
        ) : (
          <>
            <button className={styles.deleteBtn} onClick={() => setShowDeleteConfirm(true)}>
              Delete
            </button>
            {onDuplicate && (
              <button className={styles.duplicateBtn} onClick={handleDuplicate}>
                ⧉ Duplicate
              </button>
            )}
            <div className={styles.footerRight}>
              <button className={styles.cancelBtn} onClick={onClose}>
                Cancel
              </button>
              <button
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={!title.trim() || isSaving || isCancelled}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface RevisionItemProps {
  revision: BlockRevision;
  index: number;
}

function RevisionItem({ revision, index }: RevisionItemProps) {
  const snapshot = revision.snapshot;
  if (!snapshot) return null;

  const changedFields = revision.changedFields || [];

  const getChangeIcon = () => {
    if (changedFields.includes('startTime')) return '⏱';
    if (changedFields.includes('duration')) return '↔';
    if (changedFields.includes('title')) return '✏';
    return '•';
  };

  return (
    <motion.div
      className={styles.revisionItem}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.05 }}
    >
      <span className={styles.revisionIcon}>{getChangeIcon()}</span>
      <span className={styles.revisionTitle}>{snapshot.title}</span>
      <span className={styles.revisionTime}>{formatTimeFromMinutes(snapshot.startTime)}</span>
    </motion.div>
  );
}
